import { differenceInMonths, subMonths } from 'date-fns'
import { useMemo, type JSX } from 'react';
import { Area, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from 'recharts'
import { createPlaceholderData } from '~/features/visualizations/placeholder-data'
import { formatDateBySeries } from '~/helpers/dateHelper'
import { useAnalytics } from '~/modules/analytics/hooks/useAnalytics'
import { AnalyticsQueryFilterContext } from '~/modules/analytics/utils'
import { useLoader } from '~/modules/loader'

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  Y_AXIS_DEFAULT_PROPS,
} from '@hap/components'
import { toPercent } from '@hap/utils'

const DEFAULT_SERIES = 'week'

export function EvaluationScores(props: AnalyticsQueryFilterContext): JSX.Element {
  const trendsTimespan = useMemo(() => {
    if (
      differenceInMonths(new Date(props.timespan.start), new Date(props.timespan.end)) < 3
    ) {
      return {
        start: subMonths(new Date(props.timespan.end), 3),
        end: props.timespan.end,
      }
    } else {
      return props.timespan
    }
  }, [props.timespan])

  const { data, loading } = useAnalytics({
    timespan: trendsTimespan,
    dataset: 'evaluationCriteriaScores',
    filters: props.filters,
    series: DEFAULT_SERIES,
    aggregations: {
      avg: {
        field: 'score',
        op: 'avg',
        dataset: 'evaluationCriteriaScores',
      },
      upper: {
        field: 'score',
        op: 'p25',
        dataset: 'evaluationCriteriaScores',
      },
      lower: {
        field: 'score',
        op: 'p75',
        dataset: 'evaluationCriteriaScores',
      },
    },
  })

  const formattedData = useMemo(() => {
    return data
      ? data.map((d) => {
          const time = formatDateBySeries(d.time, DEFAULT_SERIES)
          const avg = d.avg ?? 0
          return {
            time,
            avg: avg,
            boundary: [d.upper ?? avg, d.lower ?? avg],
          }
        })
      : createPlaceholderData(trendsTimespan, DEFAULT_SERIES)
  }, [data])

  useLoader(loading)

  return (
    <ChartContainer
      config={{
        avg: {
          label: 'Avg Score',
          color: '#D8BFD8',
          unit: '%',
          formatter: (value) => toPercent(value),
        },
        boundary: {
          label: 'Boundary',
          color: 'var(--sky-blue)',
          hidden: true,
        },
      }}
      className="h-[200px] w-full"
    >
      <ComposedChart data={formattedData}>
        <Area
          dataKey="boundary"
          fillOpacity={0.1}
          strokeWidth={0.5}
          isAnimationActive={false}
          fill="var(--sky-blue)"
          stroke="var(--sky-blue)"
        />
        <CartesianGrid />
        <Line
          dataKey="avg"
          stroke="var(--sky-blue)"
          dot={false}
          strokeWidth={2}
          isAnimationActive={false}
        />
        <XAxis dataKey="time" strokeOpacity={0.3} />
        <YAxis
          orientation="right"
          dataKey="avg"
          domain={[0, 1]}
          {...Y_AXIS_DEFAULT_PROPS}
          tickFormatter={toPercent}
          strokeOpacity={0.3}
        />
        <ChartTooltip content={<ChartTooltipContent />} />
      </ComposedChart>
    </ChartContainer>
  )
}
